/**
 * Calculates number of segments for a circle with given radius.
 * Bigger radius requires more segments to look smooth.
 */ export const radiusToSegmentCount = (radius)=>Math.max(32, Math.ceil(Math.sqrt(radius)) * 8);
/**
 * Creates a circle polygon with given center, radius and number of segments.
 * We don't draw Three.js paths directly because we need to get rid of overlapping areas first.
 */ export const createCirclePolygon = (circle, segments = radiusToSegmentCount(circle.radius))=>{
    const { center: { x, y }, radius } = circle;
    const points = [];
    for(let i = 0; i < segments; i++){
        // Correction to match calculations from Three.js
        const angleCorrection = Math.PI / 2;
        const angle = i / segments * Math.PI * 2 + angleCorrection;
        points.push([
            x + Math.cos(angle) * radius,
            y + Math.sin(angle) * radius
        ]);
    }
    // Close the polygon
    const firstPoint = points[0];
    if (firstPoint !== undefined) {
        points.push(firstPoint);
    }
    return [
        points
    ];
};
export const createRectPolygon = (rectangle)=>{
    const { center: { x, y }, width, height } = rectangle;
    const widthHalf = width / 2;
    const heightHalf = height / 2;
    const firstPoint = [
        x - widthHalf,
        y + heightHalf
    ];
    return [
        [
            firstPoint,
            [
                x + widthHalf,
                y + heightHalf
            ],
            [
                x + widthHalf,
                y - heightHalf
            ],
            [
                x - widthHalf,
                y - heightHalf
            ],
            firstPoint
        ]
    ];
};
export const createRoundedRectPolygon = (roundedRectangle)=>{
    const { center: { x, y }, width, height, cornerRadius } = roundedRectangle;
    const widthHalf = width / 2;
    const heightHalf = height / 2;
    const segments = radiusToSegmentCount(cornerRadius);
    // Same process as for the circle but moving the centre for each quarter
    const points = [];
    for(let i = 0; i < segments; i++){
        // Correction to match calculations from Three.js
        const angleCorrection = Math.PI / 2;
        const angle = i / segments * Math.PI * 2 + angleCorrection;
        if (angle < Math.PI) {
            points.push([
                x - widthHalf + cornerRadius + Math.cos(angle) * cornerRadius,
                y + heightHalf - cornerRadius + Math.sin(angle) * cornerRadius
            ]);
        } else if (angle < Math.PI * 3 / 2) {
            points.push([
                x - widthHalf + cornerRadius + Math.cos(angle) * cornerRadius,
                y - heightHalf + cornerRadius + Math.sin(angle) * cornerRadius
            ]);
        } else if (angle < Math.PI * 2) {
            points.push([
                x + widthHalf - cornerRadius + Math.cos(angle) * cornerRadius,
                y - heightHalf + cornerRadius + Math.sin(angle) * cornerRadius
            ]);
        } else {
            points.push([
                x + widthHalf - cornerRadius + Math.cos(angle) * cornerRadius,
                y + heightHalf - cornerRadius + Math.sin(angle) * cornerRadius
            ]);
        }
    }
    // Close the polygon
    const firstPoint = points[0];
    if (firstPoint !== undefined) {
        points.push(firstPoint);
    }
    return [
        points
    ];
};
export const isPolygon = (geometry)=>typeof geometry[0]?.[0]?.[0] === 'number';
export const isMultiPolygon = (geometry)=>Array.isArray(geometry[0]?.[0]?.[0]);
/**
 * Rotates geometry around the origin
 */ export const rotateGeometry = (geometry, angle, origin = {
    x: 0,
    y: 0
})=>{
    const s = Math.sin(angle);
    const c = Math.cos(angle);
    const { x: ox, y: oy } = origin;
    const rotatePoint = ([x = 0, y = 0])=>[
            (x - ox) * c - (y - oy) * s + ox,
            (x - ox) * s + (y - oy) * c + oy
        ];
    if (isPolygon(geometry)) {
        return geometry.map((polygonPoints)=>polygonPoints.map(rotatePoint));
    } else {
        return geometry.map((polygon)=>polygon.map((polygonPoints)=>polygonPoints.map(rotatePoint)));
    }
};

//# sourceMappingURL=shapes.js.map